import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from './BackButton.js';
import CDMorph from './landingAnimation.js';
import usePageSeo from './seo.js';
import SupportLine from './support_line.js';
import './index.css';

const UPLOAD_DESC =
  'Upload your songs and cover art to MP3JUUG, then generate one-time links to '
  + 'write onto JUUG cards. Distribute your music yourself — no label, no middleman.';

const STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to distribute your music with MP3JUUG',
  description: UPLOAD_DESC,
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Upload your music', text: 'Add your .wav or .mp3 files and a cover art image — we apply the cover art.' },
    { '@type': 'HowToStep', position: 2, name: 'Order JUUGs', text: 'Buy blank NFC cards in a pack sized to your release.' },
    { '@type': 'HowToStep', position: 3, name: 'Generate links', text: 'Once your JUUGs arrive, create one one-time link per card.' },
    { '@type': 'HowToStep', position: 4, name: 'Hand out the cards', text: 'A fan taps a card and your songs are added to their library.' },
  ],
};


// reads an audio file's duration client-side (seconds) without uploading it.
// Some MP3s (certain VBR encodings without an accurate header) report
// duration as Infinity on loadedmetadata until the browser is forced to
// seek and actually index the file — the standard workaround is seeking
// past the end and reading the corrected value off 'durationchange'.
function getAudioDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'metadata';

    const cleanup = (value) => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('error', onError);
      resolve(value);
    };

    const onLoadedMetadata = () => {
      if (Number.isFinite(audio.duration)) {
        cleanup(audio.duration);
      } else {
        audio.currentTime = Number.MAX_SAFE_INTEGER;
      }
    };

    const onDurationChange = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        cleanup(audio.duration);
      }
    };

    const onError = () => cleanup(null);

    const timeoutId = setTimeout(() => cleanup(null), 8000);

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('error', onError);
    audio.src = url;
  });
}

function ArtistUpload() {
  const [files, setUpload] = useState([]);
  const [artistName, setArtistName] = useState('');
  const [email, setEmail] = useState('');
  const [coverArt, setCoverArt] = useState(null);
  const [alertEmail, setAlertEmail] = useState(null);
  const [uploaded, setUploaded] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [priorOrders, setPriorOrders] = useState(null);
  const [freeJuug, setFreeJuug] = useState(null);
  const [firstUpload, setFirstUpload] = useState(false);
  const navigate = useNavigate();
  usePageSeo({ title: 'Upload your music | MP3JUUG', description: UPLOAD_DESC, jsonLd: STRUCTURED_DATA });
  const artistNameId = useId();
  const emailId = useId();
  const emailErrorId = useId();
  const filesId = useId();
  const coverArtId = useId();

  // upload function
async function upload(files, coverArt) {
  // Normalize
  files = Array.from(files || []);
  if (!files.length) {
    console.error("No files received by upload");
    return;
  }
  // coverArt comes in as a FileList (from <input type="file">, no `multiple`)
  const coverArtFile = coverArt && coverArt[0] ? coverArt[0] : null;

  // always send a duration param per file (even if empty) so filename[]/duration[]
  // stay positionally aligned server-side — omitting one on failure would shift
  // every duration after it onto the wrong file
  const durations = await Promise.all(files.map(getAudioDuration));
  const params = files
    .map((f, i) => `filename=${encodeURIComponent(f.name)}&duration=${durations[i] ?? ''}`)
    .join("&");
  const coverArtParam = coverArtFile ? `&coverArt=${encodeURIComponent(coverArtFile.name)}` : '';
  // FOR LOCAL http://localhost:8080/
  const url = `/uploadv2?email=${encodeURIComponent(email)}&artistName=${encodeURIComponent(artistName)}&${params}${coverArtParam}`;

  // GET signed URLs from /uploadv2 instead of POST
  const r = await fetch(url); //
  const data = await r.json();
  const signedUrls = data.signedUrls;
  console.log("data processing...");
  // Upload raw binary files to signed URLs
  for (let i = 0; i < files.length; i++) {
    await fetch(signedUrls[i], {
      method: "PUT",
      body: files[i],
      headers: { "Content-Type": files[i].type }
    });
  }
  // Upload the actual cover art bytes to its own signed URL
  if (coverArtFile && data.coverArtUploadUrl) {
    await fetch(data.coverArtUploadUrl, {
      method: "PUT",
      body: coverArtFile,
      headers: { "Content-Type": coverArtFile.type }
    });
  }

  return data; // { signedUrls, coverArtUploadUrl, firstUpload }
}


// perform upload
async function fetchData() {
  try {
    setUploaded(true);
    const uploadData = await upload(files, coverArt);
    setFirstUpload(!!uploadData?.firstUpload);
    setUploadDone(true);
    // has this email ordered JUUGs before? drives the re-order prompt.
    // and is the free first-pack offer open + unclaimed for this email?
    try {
      const [mine, free] = await Promise.all([
        fetch(`/media/mine?email=${encodeURIComponent(email)}`),
        fetch(`/free-juugs/status?email=${encodeURIComponent(email)}`),
      ]);
      if (mine.ok) setPriorOrders(await mine.json());
      if (free.ok) setFreeJuug(await free.json());
    } catch { /* prompt just falls back to the generic copy */ }
  } catch (err) {
    console.log(err);
  } finally {
    setUploaded(false);
  }
}

  // validate then perform upload
  function handleSubmit(e) {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email.trim() === '') {
      console.log('email is blank')
      setAlertEmail(true);
      return true; // stop submission
    } else if (emailRegex.test(email) === false){ 
      console.log('email is not in proper format');
      setAlertEmail(false);
      return false;
    } else {
      setAlertEmail(null);
      
    }
    fetchData();
  
  }
  if (uploaded) return <UploadingSongs/>;

  if (uploadDone) {
    const returning = priorOrders && priorOrders.count > 0;
    // First-ever upload for this email only — the free pack is a new-artist perk.
    const showFree = firstUpload && !returning && freeJuug && freeJuug.configured
      && !freeJuug.claimed && !freeJuug.alreadyOrdered;
    const freeCount = freeJuug?.count || 5;
    return (
      <main className='page'>
        <div className='page-content'>
          <BackButton fallback='/' />
          <div className='card'>
            <h1>Upload complete</h1>
            <p className='lead'>
              Your music is in. Next: cop JUUGs (the NFC cards), then write a one-time
              link onto each one.
            </p>
            <div className='notice notice-warn' role='note'>
              <span aria-hidden='true'>⚠️</span>
              <span>
                <strong>Order your JUUGs before you make links.</strong>
                Links are one-time use and can’t be reissued — create them only once the
                cards are in hand.
              </span>
            </div>
          </div>

          {showFree ? (
            <div className='card'>
              <h2>Claim your {freeCount} free JUUGs</h2>
              <p>
                First release? Your first pack of {freeCount} JUUGs (the NFC cards your
                links go on) is on us — just give us a US shipping address. One pack per artist.
              </p>
              <button
                type='button'
                className='btn btn-primary'
                onClick={() => navigate(`/free-juugs?email=${encodeURIComponent(email)}`)}
              >
                Claim {freeCount} free JUUGs
              </button>
              <button
                type='button'
                className='btn btn-secondary'
                onClick={() => navigate('/media')}
              >
                Or buy a bigger pack
              </button>
            </div>
          ) : (
            <div className='card'>
              {returning ? (
                <>
                  <h2>Order more JUUGs</h2>
                  <p>
                    You’ve ordered JUUGs before ({priorOrders.count}
                    {priorOrders.count === 1 ? ' order' : ' orders'}). Grab another pack
                    whenever you’re running low.
                  </p>
                </>
              ) : (
                <>
                  <h2>Cop JUUGs</h2>
                  <p>
                    JUUGs are the NFC cards your links go on. Buy a pack sized to
                    your release — from $5 for 5.
                  </p>
                </>
              )}
              <button type='button' className='btn btn-primary' onClick={() => navigate('/media')}>
                {returning ? 'Order more JUUGs' : 'Cop JUUGs'}
              </button>
            </div>
          )}

          <button
            type='button'
            className='btn btn-secondary'
            onClick={() => { setUploadDone(false); setPriorOrders(null); setUpload([]); setCoverArt(null); }}
          >
            Upload another release
          </button>
          <button type='button' className='btn btn-secondary' onClick={() => navigate('/link')}>
            Go to links
          </button>
          <SupportLine />
        </div>
      </main>
    );
  }

  return (
    <main className='page'>
      <div className='page-content'>
        <BackButton fallback='/' />
        <div className='card'>
          <h1>Artists, are you tired of making .0003 per stream?</h1>
          <p>With MP3JUUG you can DIY: Distribute it yourself.</p>

          <form onSubmit={handleSubmit}>
            <div className='field'>
              <label className='label' htmlFor={artistNameId}>Artist name</label>
              <input
                id={artistNameId}
                className='input'
                type='text'
                autoComplete='name'
                value={artistName}
                onChange={(e) => setArtistName(e.target.value)}
              />
            </div>

            <div className='field'>
              <label className='label' htmlFor={emailId}>Upload email</label>
              <input
                id={emailId}
                className='input'
                type='email'
                autoComplete='email'
                inputMode='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby={alertEmail !== null ? emailErrorId : undefined}
                aria-invalid={alertEmail !== null}
              />
              {alertEmail === true && (
                <p className='error-text' id={emailErrorId}>
                  Please enter your email address to use for uploads.
                </p>
              )}
              {alertEmail === false && (
                <p className='error-text' id={emailErrorId}>
                  Please enter an email address with proper format, e.g. artist@example.com.
                </p>
              )}
            </div>

            <div className='field'>
              <label className='label' htmlFor={filesId}>Music files (.wav or .mp3)</label>
              <input
                id={filesId}
                className='input'
                type='file'
                accept='.wav,.mp3,audio/*'
                multiple
                onChange={(e) => setUpload(e.target.files)}
              />
            </div>

            <div className='field'>
              <label className='label' htmlFor={coverArtId}>Cover art</label>
              <input
                id={coverArtId}
                className='input'
                type='file'
                accept='image/*'
                onChange={(e) => setCoverArt(e.target.files)}
              />
            </div>

            <button type='submit' className='btn btn-primary'>Upload</button>
          </form>
        </div>

        <button
          type='button'
          className='btn btn-secondary'
          onClick={() => {navigate('/link');}}
        >
          Get your links now
        </button>
        <p className='help-text'>Place your links on physical media.</p>
        <SupportLine />
      </div>
    </main>
  );
}

function UploadingSongs() {
  return (
    <main className='page' aria-live='polite'>
      <div className='page-content'>
        <div className='card' style={{textAlign: 'center'}}>
          <div aria-hidden='true'>
            <CDMorph/>
          </div>
          <p>Uploading your music...</p>
        </div>
      </div>
    </main>
  );
}

export default ArtistUpload;
